import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customerService } from '../../services/customerService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import type { Customer, CustomerFilters, GetCustomersResponse } from '../../types/customer';

interface CustomerListProps {
  onSelectCustomer?: (customer: Customer) => void;
  onEditCustomer?: (customer: Customer) => void;
}

const customerStatusStyle: Record<string, React.CSSProperties> = {
  ACTIVE:   { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' },
  INACTIVE: { background: 'rgba(80,80,80,0.15)',   color: '#555570', border: '1px solid rgba(80,80,80,0.2)'   },
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

export const CustomerList: React.FC<CustomerListProps> = ({
  onSelectCustomer,
  onEditCustomer
}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<CustomerFilters>({
    page: 1,
    limit: 10,
    search: '',
    status: undefined
  });

  const { data, isLoading, error } = useQuery<GetCustomersResponse>({
    queryKey: ['customers', filters],
    queryFn: () => customerService.getCustomers(filters)
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: customerService.deleteCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    }
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handleStatusFilter = (status: CustomerFilters['status']) => {
    setFilters(prev => ({ ...prev, status, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDeleteCustomer = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await deleteCustomerMutation.mutateAsync(id);
      } catch (error) {
        console.error('Error deleting customer:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Customers</div>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ height: 36, background: 'rgba(241,245,249,1)', borderRadius: 3 }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 6, padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a' }}>
          Error loading customers. Please try again.
        </p>
      </div>
    );
  }

  const customers = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Customers</div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
            {pagination?.total ?? 0} total customers
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search customers..."
              value={filters.search}
              onChange={handleSearch}
              style={{
                paddingLeft: 34,
                paddingRight: 12,
                paddingTop: 7,
                paddingBottom: 7,
                background: '#f4efe2',
                border: '1px solid rgba(92,78,51,0.25)',
                borderRadius: 4,
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                color: '#5b5a52',
                outline: 'none',
                width: 200,
              }}
            />
            <svg
              width="14"
              height="14"
              fill="none"
              stroke="rgba(217,119,6,0.35)"
              viewBox="0 0 24 24"
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Status Filter */}
          <select
            value={filters.status || ''}
            onChange={(e) => handleStatusFilter(e.target.value as CustomerFilters['status'] || undefined)}
            style={{
              padding: '7px 12px',
              background: '#f4efe2',
              border: '1px solid rgba(92,78,51,0.25)',
              borderRadius: 4,
              fontFamily: "'Inter', sans-serif",
              fontSize: 12,
              color: '#5b5a52',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>
      </div>

      {customers.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <svg
            width="40"
            height="40"
            fill="none"
            stroke="rgba(217,119,6,0.25)"
            viewBox="0 0 24 24"
            style={{ margin: '0 auto 12px', display: 'block' }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.2em', color: '#6b6a5c' }}>NO CUSTOMERS FOUND</p>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#8a8778', marginTop: 6 }}>Get started by adding your first customer.</p>
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Contact</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Outstanding</th>
                  <th style={thStyle}>Invoices</th>
                  <th style={thStyle}>Created</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer: Customer) => (
                  <tr
                    key={customer.id}
                    style={{ transition: 'background 0.15s', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    onClick={() => onSelectCustomer?.(customer)}
                  >
                    <td style={tdStyle}>
                      <div style={{ color: '#1c2033', fontWeight: 500 }}>{customer.name}</div>
                      {customer.taxId && (
                        <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>Tax ID: {customer.taxId}</div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {customer.email && <div>{customer.email}</div>}
                      {customer.phone && <div style={{ marginTop: 2, color: '#6b6a5c', fontSize: 11 }}>{customer.phone}</div>}
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        ...(customerStatusStyle[customer.status] || customerStatusStyle.INACTIVE),
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: 3,
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 8,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                      }}>
                        {customer.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: '#fbbf24' }}>
                        {formatCurrency(customer.outstandingBalance)}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {customer._count?.invoices || 0}
                    </td>
                    <td style={tdStyle}>
                      {formatDate(customer.createdAt)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditCustomer?.(customer);
                          }}
                          style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5b5a52', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCustomer(customer.id);
                          }}
                          style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
                Showing {((pagination.currentPage - 1) * filters.limit!) + 1}–{Math.min(pagination.currentPage * filters.limit!, pagination.total)} of {pagination.total} customers
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrev}
                  style={{
                    padding: '4px 12px',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 9,
                    letterSpacing: '0.1em',
                    background: 'rgba(239,232,213,0.5)',
                    color: pagination.hasPrev ? '#8a8778' : '#c9c2ab',
                    border: '1px solid rgba(92,78,51,0.22)',
                    borderRadius: 3,
                    cursor: pagination.hasPrev ? 'pointer' : 'not-allowed',
                    opacity: pagination.hasPrev ? 1 : 0.4,
                  }}
                >
                  Prev
                </button>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNext}
                  style={{
                    padding: '4px 12px',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 9,
                    letterSpacing: '0.1em',
                    background: 'rgba(239,232,213,0.5)',
                    color: pagination.hasNext ? '#8a8778' : '#c9c2ab',
                    border: '1px solid rgba(92,78,51,0.22)',
                    borderRadius: 3,
                    cursor: pagination.hasNext ? 'pointer' : 'not-allowed',
                    opacity: pagination.hasNext ? 1 : 0.4,
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
